// src/GlassMenu.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./pill.css";

import CommonwealthLogo from '../assets/images/cwealthlogo.png';

/* =========================
   Helpers (unchanged)
   ========================= */
function clamp01(n) {
  return Math.min(1, Math.max(0, n));
}

function parseCssColorToRgb(color) {
  if (!color) return null;

  const m = color.match(/rgba?\(([^)]+)\)/i);
  if (m) {
    const parts = m[1].split(",").map((p) => p.trim());
    const r = Number(parts[0]);
    const g = Number(parts[1]);
    const b = Number(parts[2]);
    const a = parts.length >= 4 ? Number(parts[3]) : 1;
    if ([r, g, b, a].some((v) => Number.isNaN(v))) return null;
    return { r, g, b, a: clamp01(a) };
  }

  if (color[0] === "#") {
    const hex = color.slice(1);
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      return { r, g, b, a: 1 };
    }
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return { r, g, b, a: 1 };
    }
  }

  return null;
}

function luminance({ r, g, b }) {
  const srgb = [r, g, b].map((v) => v / 255);
  const lin = srgb.map((c) =>
    c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

function isTransparentBg(bg) {
  if (!bg) return true;
  if (bg === "transparent") return true;
  const rgb = parseCssColorToRgb(bg);
  if (!rgb) return true;
  return rgb.a <= 0.02;
}

// Walk up DOM until we find a non-transparent background-color.
function findEffectiveBgColor(el) {
  let cur = el;
  while (cur && cur !== document.documentElement) {
    const cs = window.getComputedStyle(cur);
    const bg = cs.backgroundColor;
    if (!isTransparentBg(bg)) return bg;
    cur = cur.parentElement;
  }

  const bodyBg = window.getComputedStyle(document.body).backgroundColor;
  if (!isTransparentBg(bodyBg)) return bodyBg;

  const htmlBg = window.getComputedStyle(document.documentElement).backgroundColor;
  if (!isTransparentBg(htmlBg)) return htmlBg;

  return "rgb(255,255,255)";
}

export default function GlassMenu({ visible = true }) {
  const barRef = useRef(null);
  const itemRefs = useRef([]);

  const location = useLocation();

  const [onLightBg, setOnLightBg] = useState(false);
  const [indicator, setIndicator] = useState({ x: 0, w: 0, show: false });

  const items = useMemo(
    () => [
      { label: "Mission", to: "/manifesto" },
      { label: "Waitlist", to: "/waitlist" },
    ],
    []
  );

  /* =========================
     Background luminance detect
     ========================= */
  useEffect(() => {
    let raf = 0;

    const measure = () => {
      raf = 0;
      const bar = barRef.current;
      if (!bar) return;

      const r = bar.getBoundingClientRect();
      const x = Math.round(r.left + r.width / 2);
      const y = Math.round(r.top + r.height / 2);

      const stack = document.elementsFromPoint(x, y);

      // pick first element under bar that's not itself
      let under = null;
      for (const el of stack) {
        if (bar === el || bar.contains(el)) continue;
        under = el;
        break;
      }

      const bgCss = findEffectiveBgColor(under || document.body);
      const rgb = parseCssColorToRgb(bgCss);

      if (!rgb) {
        setOnLightBg(false);
        return;
      }

      setOnLightBg(luminance(rgb) > 0.8);
    };

    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(measure);
    };

    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);

    const id = window.setInterval(schedule, 250);

    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.clearInterval(id);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  /* =========================
     Hover pill positioning
     ========================= */
  const moveIndicator = (idx) => {
    const bar = barRef.current;
    const el = itemRefs.current[idx];
    if (!bar || !el) return;

    const barRect = bar.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();

    setIndicator({
      x: elRect.left - barRect.left,
      w: elRect.width,
      show: true,
    });
  };

  const hideIndicator = () => {
    setIndicator((s) => ({ ...s, show: false }));
  };

  // keep indicator aligned if user resizes while hovering
  useEffect(() => {
    const onResize = () => {
      // find currently hovered/focused element by checking show + nearest match
      // simplest: if showing, recompute based on the element under the pill center
      // (or just hide; but we keep it stable by remeasuring the last known target)
      // We'll remeasure by scanning for the focused element first:
      const active = document.activeElement;
      const bar = barRef.current;
      if (!bar) return;

      const idx = itemRefs.current.findIndex((n) => n && (n === active || n.contains(active)));
      if (idx >= 0) moveIndicator(idx);
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // on route change, clear hover pill
  useEffect(() => {
    hideIndicator();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.hash]);

  return (
    <nav
  ref={barRef}
  className={`glassBar ${onLightBg ? "glassBar--green" : ""} ${
    visible ? "glassBar--show" : "glassBar--hide"
  }`}
  aria-label="Commonwealth menu"
  onMouseLeave={hideIndicator}
>
  {/* LEFT: Logo */}
  <div className="glassBar__left">
    <span className="glassBar__logo">
    <Link to={"/"}>
      <img
        src={CommonwealthLogo}
        alt="Commonwealth Logo"
        style={{ maxWidth: "35%"}}
      />
      </Link>
    </span>
  </div>

  {/* RIGHT: Menu Items */}
  <div className="glassBar__right">
    <span
      className={`glassBar__hoverPill ${
        indicator.show ? "glassBar__hoverPill--show" : ""
      }`}
      style={{
        transform: `translateY(-50%) translateX(${indicator.x}px)`,
        width: `${indicator.w}px`,
      }}
      aria-hidden="true"
    />

    {items.map((it, idx) => (
      <Link
        key={it.label}
        to={it.to}
        ref={(node) => (itemRefs.current[idx] = node)}
        className="glassBar__item"
        onMouseEnter={() => moveIndicator(idx)}
        onFocus={() => moveIndicator(idx)}
        onBlur={hideIndicator}
      >
        {it.label}
      </Link>
    ))}
  </div>
</nav>
  );
}
